// src/components/DashboardImageManager.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Typography, Paper, Divider, Chip, Button, IconButton, Tooltip,
    Dialog, DialogContent, DialogActions, DialogTitle, DialogContentText,
    CircularProgress, Alert, Snackbar,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsIcon from '@mui/icons-material/Collections';

import {
    getDashboardImages, addDashboardImage, replaceDashboardImage,
    deleteDashboardImage, reorderDashboardImages,
} from '../services/dashboardService';

const ACCEPT = 'image/*,video/mp4,video/quicktime';

/** The RTDB list mixes images and videos; pick the right preview element per URL. */
const isVideo = (url: string) => /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);

const fileName = (url: string) => {
    try {
        return decodeURIComponent(new URL(url).pathname.split('/').pop() || url);
    } catch {
        return url.split('/').pop() || url;
    }
};

export default function DashboardImageManager(): React.ReactElement {
    const [images, setImages] = useState<string[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState<string | null>(null);
    const [preview, setPreview] = useState<number | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

    // One hidden input for "add", one for "replace at index".
    const addInputRef = useRef<HTMLInputElement>(null);
    const replaceInputRef = useRef<HTMLInputElement>(null);
    const replaceIndexRef = useRef<number | null>(null);

    const load = useCallback(async () => {
        try {
            setImages(await getDashboardImages());
            setError(null);
        } catch (err: any) {
            setError(err?.message ?? 'Failed to load dashboard images');
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    /** Every mutation returns the new list, so we just swap it in. */
    const run = async (action: () => Promise<string[]>, successMessage: string) => {
        setBusy(true);
        setError(null);
        try {
            setImages(await action());
            setToast(successMessage);
        } catch (err: any) {
            setError(err?.message ?? 'Operation failed');
        } finally {
            setBusy(false);
        }
    };

    const onAddFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-picking the same file
        if (file) void run(() => addDashboardImage(file), 'Image added');
    };

    const onReplaceFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        const index = replaceIndexRef.current;
        e.target.value = '';
        replaceIndexRef.current = null;
        if (file && index !== null) void run(() => replaceDashboardImage(index, file), 'Image replaced');
    };

    const startReplace = (index: number) => {
        replaceIndexRef.current = index;
        replaceInputRef.current?.click();
    };

    const move = (index: number, delta: number) => {
        if (!images) return;
        const target = index + delta;
        if (target < 0 || target >= images.length) return;
        const next = [...images];
        [next[index], next[target]] = [next[target], next[index]];
        void run(() => reorderDashboardImages(next), 'Order updated');
    };

    const doDelete = (index: number) => {
        setConfirmDelete(null);
        void run(() => deleteDashboardImage(index), 'Image deleted');
    };

    return (
        <Box sx={{ mt: 5 }}>
            <input ref={addInputRef} type="file" accept={ACCEPT} hidden onChange={onAddFile} />
            <input ref={replaceInputRef} type="file" accept={ACCEPT} hidden onChange={onReplaceFile} />

            {/* ── Section label ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#174143' }}>
                    Dashboard Images
                </Typography>
                <Divider sx={{ flex: 1 }} />
                {images && (
                    <Chip
                        label={`${images.length} item${images.length === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ background: '#174143', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }}
                    />
                )}
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddPhotoAlternateIcon />}
                    disabled={busy}
                    onClick={() => addInputRef.current?.click()}
                    sx={{ background: '#174143', '&:hover': { background: '#2a6b6f' }, borderRadius: 2, fontWeight: 600 }}
                >
                    Add
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {images === null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : images.length === 0 ? (
                <Paper elevation={0} sx={{ p: 6, borderRadius: 4, textAlign: 'center', border: '1px dashed #cfd8dc', background: '#fff' }}>
                    <CollectionsIcon sx={{ fontSize: 48, color: '#b0bec5', mb: 1 }} />
                    <Typography color="text.secondary">No dashboard images yet. Add one to get started.</Typography>
                </Paper>
            ) : (
                <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, opacity: busy ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                    {images.map((url, i) => (
                        <Paper
                            key={`${url}-${i}`}
                            elevation={0}
                            sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e0e0e0', background: '#fff', display: 'flex', flexDirection: 'column' }}
                        >
                            <Box
                                sx={{ position: 'relative', height: 150, background: '#eceff1', cursor: 'pointer' }}
                                onClick={() => setPreview(i)}
                            >
                                {isVideo(url) ? (
                                    <Box component="video" src={url} muted sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <Box component="img" src={url} alt={fileName(url)} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                )}
                                <Chip
                                    label={`#${i + 1}`}
                                    size="small"
                                    sx={{ position: 'absolute', top: 8, left: 8, background: 'rgba(23,65,67,0.85)', color: '#fff', fontWeight: 700, fontSize: '0.68rem' }}
                                />
                            </Box>

                            <Box sx={{ px: 1.5, pt: 1 }}>
                                <Typography variant="caption" noWrap title={url} sx={{ display: 'block', color: 'text.secondary' }}>
                                    {fileName(url)}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, py: 0.5, mt: 'auto' }}>
                                <Tooltip title="Move left">
                                    <span>
                                        <IconButton size="small" disabled={busy || i === 0} onClick={() => move(i, -1)}>
                                            <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Move right">
                                    <span>
                                        <IconButton size="small" disabled={busy || i === images.length - 1} onClick={() => move(i, 1)}>
                                            <ArrowForwardIosIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Box sx={{ flex: 1 }} />
                                <Tooltip title="View">
                                    <IconButton size="small" onClick={() => setPreview(i)}>
                                        <VisibilityIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Replace">
                                    <span>
                                        <IconButton size="small" disabled={busy} onClick={() => startReplace(i)}>
                                            <SwapHorizIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <span>
                                        <IconButton size="small" color="error" disabled={busy} onClick={() => setConfirmDelete(i)}>
                                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            )}

            {/* ── Full-size preview ── */}
            <Dialog open={preview !== null} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {preview !== null && images ? fileName(images[preview]) : ''}
                    </Typography>
                    <IconButton size="small" onClick={() => setPreview(null)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', background: '#000' }}>
                    {preview !== null && images && (
                        isVideo(images[preview])
                            ? <Box component="video" src={images[preview]} controls autoPlay sx={{ maxWidth: '100%', maxHeight: '70vh' }} />
                            : <Box component="img" src={images[preview]} alt="" sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
                    )}
                </DialogContent>
                <DialogActions>
                    {preview !== null && images && (
                        <Button size="small" href={images[preview]} target="_blank" rel="noopener noreferrer">Open original</Button>
                    )}
                    <Button size="small" onClick={() => setPreview(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete confirmation ── */}
            <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Delete this image?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        It will be removed from the dashboard and deleted from storage. This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={() => confirmDelete !== null && doDelete(confirmDelete)}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast !== null}
                autoHideDuration={3000}
                onClose={() => setToast(null)}
                message={toast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}
